// AdminLeaderboards.tsx — Phase 11G platform-wide top-earning agents.
//
// Reads /admin/leaderboards/agents?window=&pillar=&limit=
// Same component is reused with prop `scope='downline'` for the
// agent-side "downline leaderboard" page (hits /me-agent/leaderboards/downline).
//
// Phase 2 UI overhaul (2026-05-15): visual surfaces realigned with the
// player/agent arena-tinted glassmorphic look — hard borders → soft
// arena-tinted insetting rings, neutral palette → arena/neon tokens,
// dense rows → spaced-out hover-highlight rows, fuchsia accent →
// admin's violet identity. Data + API calls unchanged.

import { useCallback, useEffect, useState } from 'react';
import { levelName } from '../../lib/levelNames';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';

type WindowKey = '24h' | '7d' | '30d' | 'all';
type Pillar = 'all' | 'platform_fee' | 'win_loss' | 'offer_reward';

interface Row {
  agent_id: number;
  agent_username: string;
  agent_level: number;
  parent_agent_id: number | null;
  matches: number;
  true_net: string | number;
  platform_fee_net: string | number;
  win_loss_net: string | number;
  offer_reward_net: string | number;
}

interface Resp {
  window: WindowKey;
  pillar: Pillar;
  rows: Row[];
  // Batch 6 #27: pagination metadata.
  limit?: number;
  offset?: number;
  hasMore?: boolean;
}

const PAGE_SIZE = 20;

const WINDOWS: Array<{ k: WindowKey; label: string }> = [
  { k: '24h', label: '24h' }, { k: '7d', label: '7d' }, { k: '30d', label: '30d' }, { k: 'all', label: 'All' },
];
const PILLARS: Array<{ k: Pillar; label: string; icon: string }> = [
  { k: 'all',          label: 'All pillars',   icon: 'view_in_ar' },
  { k: 'platform_fee', label: 'Platform fee',  icon: 'percent' },
  { k: 'win_loss',     label: 'Win / Loss',    icon: 'casino' },
  { k: 'offer_reward', label: 'Offer reward',  icon: 'redeem' },
];

const fmtSigned = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}₹${Math.abs(n).toLocaleString('en-IN')}`;

export default function LeaderboardsPanel({
  scope = 'admin',
}: {
  scope?: 'admin' | 'downline';
} = {}) {
  const [windowK, setWindowK] = useState<WindowKey>('7d');
  const [pillar, setPillar] = useState<Pillar>('all');
  const [page, setPage] = useState(0);
  const [data, setData] = useState<Resp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const endpoint = scope === 'downline' ? '/admin/me-agent/leaderboards/downline' : '/admin/leaderboards/agents';
  const isAdmin = scope === 'admin';

  // Reset page when filter changes (a different window/pillar slice has
  // its own ranking — don't carry the old page index over).
  useEffect(() => { setPage(0); }, [windowK, pillar]);

  const reload = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const offset = page * PAGE_SIZE;
      const r = await adminApi.get<Resp>(
        `${endpoint}?window=${windowK}&pillar=${pillar}&limit=${PAGE_SIZE}&offset=${offset}`
      );
      setData(r);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setLoading(false); }
  }, [windowK, pillar, page, endpoint]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {err && <Banner tone="error">{err}</Banner>}

        {/* Window + pillar pills — sticky inside panel so filters stay
            in reach while paging through long rankings. */}
        <motion.div
          initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 z-10 -mx-2 px-2 sm:-mx-3 sm:px-3 lg:-mx-4 lg:px-4 py-2
                     backdrop-blur-md
                     bg-white/70 dark:bg-arena-900/60
                     flex flex-wrap gap-2 items-center"
        >
          <span className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">Window</span>
          {WINDOWS.map(w => (
            <Pill key={w.k} active={windowK === w.k} onClick={() => setWindowK(w.k)}>{w.label}</Pill>
          ))}
          <span className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold ml-2">Pillar</span>
          {PILLARS.map(p => (
            <Pill key={p.k} active={pillar === p.k} onClick={() => setPillar(p.k)} icon={p.icon}>{p.label}</Pill>
          ))}
          <div className="flex-1" />
          <button onClick={reload}
                  className="grid place-items-center w-9 h-9 rounded-xl
                             bg-white dark:bg-arena-700/40
                             ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                             text-arena-700 dark:text-neon-200/80
                             hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
            <Icon name="refresh" size={16} />
          </button>
        </motion.div>

        {loading && !data && (
          <div className="text-center py-12 text-sm text-arena-600 dark:text-neon-200/60">
            <Icon name="progress_activity" size={20} className="animate-spin inline-block mr-2 align-middle" /> Loading…
          </div>
        )}

        {data && data.rows.length === 0 && (
          <EmptyState icon="leaderboard"
                      title="No earnings yet"
                      subtitle={isAdmin
                        ? `No settlement activity in the ${windowK} window across all agents.`
                        : `No earnings from your downline in the ${windowK} window. As your sub-agents drive matches, this board will populate.`} />
        )}

        {data && data.rows.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl
                       bg-white dark:bg-arena-700/40
                       ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                       backdrop-blur
                       overflow-hidden"
          >
            <div className="grid grid-cols-1 divide-y divide-arena-200/40 dark:divide-arena-700/40">
              {data.rows.map((r, i) => {
                const trueNet = Number(r.true_net);
                const plat    = Number(r.platform_fee_net);
                const wl      = Number(r.win_loss_net);
                const off     = Number(r.offer_reward_net);
                // Batch 6 #27: rank is GLOBAL across pages — page 2 row 0 is #21.
                const globalRank = page * PAGE_SIZE + i;
                const medal = globalRank === 0 ? '🥇' : globalRank === 1 ? '🥈' : globalRank === 2 ? '🥉' : null;
                return (
                  <div key={r.agent_id}
                       className="px-4 py-3.5 hover:bg-arena-50/60 dark:hover:bg-white/[0.03] transition flex items-center gap-3 flex-wrap">
                    <span className={`grid place-items-center w-9 h-9 rounded-full shrink-0 text-[12px] font-black tabular-nums ${
                      globalRank === 0 ? 'bg-amber-100 text-amber-800 dark:bg-amber-400/20 dark:text-amber-200'
                      : globalRank === 1 ? 'bg-arena-100/60 text-arena-700 dark:bg-arena-600/50 dark:text-neon-200/90'
                      : globalRank === 2 ? 'bg-orange-100 text-orange-700 dark:bg-orange-400/20 dark:text-orange-200'
                      : 'bg-arena-50/70 text-arena-600 dark:bg-arena-700/40 dark:text-neon-200/70'
                    }`}>
                      {medal || `#${globalRank + 1}`}
                    </span>
                    <div className="min-w-0 flex-1">
                      {/* Batch 6: routed at canonical /people/agents/:id (was legacy /agents/:id). */}
                      <Link to={`/people/agents/${r.agent_id}`}
                            className="font-bold text-arena-900 dark:text-white hover:text-violet-600 dark:hover:text-violet-300">
                        @{r.agent_username}
                      </Link>
                      <span className="ml-1.5 text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">{levelName(r.agent_level)}</span>
                      <div className="text-[11.5px] text-arena-600 dark:text-neon-200/60 mt-0.5">
                        {r.matches.toLocaleString('en-IN')} match{r.matches === 1 ? '' : 'es'}
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-3">
                      {pillar === 'all' && <>
                        <PillarChip label="Plat" value={plat} />
                        <PillarChip label="W/L"  value={wl} />
                        <PillarChip label="Offer" value={off} />
                      </>}
                    </div>
                    <div className={`text-2xl font-display font-black tabular-nums ${trueNet >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      {fmtSigned(trueNet)}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Batch 6 #27: prev/next pagination. Page index is local
            state; the backend reports hasMore so we know when to
            disable Next without a separate count query. */}
        {data && (data.rows.length > 0 || page > 0) && (
          <div className="flex items-center justify-between gap-3 pt-1">
            <div className="text-[11px] text-arena-600 dark:text-neon-200/60 tabular-nums">
              {data.rows.length > 0
                ? <>Showing <b>{page * PAGE_SIZE + 1}</b>–<b>{page * PAGE_SIZE + data.rows.length}</b></>
                : <>Page {page + 1} is empty</>}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold
                           bg-white dark:bg-arena-700/40
                           ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                           text-arena-700 dark:text-neon-200/80
                           hover:bg-arena-50 dark:hover:bg-arena-700/60
                           disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <Icon name="chevron_left" size={14} /> Prev
              </button>
              <span className="text-[11px] font-bold tabular-nums text-arena-700 dark:text-neon-200/80 px-1.5">
                Page {page + 1}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!data?.hasMore || loading}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold
                           bg-violet-600 hover:bg-violet-500 text-white
                           shadow-[0_2px_10px_rgba(139,92,246,0.4)]
                           disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-arena-200/60 dark:disabled:bg-arena-700/40 disabled:shadow-none disabled:text-arena-500 dark:text-neon-200/60 dark:disabled:text-neon-200/50
                           transition active:scale-95"
              >
                Next <Icon name="chevron_right" size={14} />
              </button>
            </div>
          </div>
        )}

        <p className="text-[11px] text-arena-600 dark:text-neon-200/50 px-1">
          Earnings sourced from <span className="font-mono">match_settlement_snapshots</span>.
          A negative number means that agent paid more out than they took in across the window.
        </p>
      </div>
    </div>
  );
}

function Pill({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon?: string; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition ring-1 ring-inset ${
              active
                ? 'bg-violet-600 text-white ring-violet-600 shadow-[0_2px_10px_rgba(139,92,246,0.4)]'
                : 'bg-white text-arena-700 ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/80 dark:ring-arena-600/40 dark:hover:bg-arena-700/60'
            }`}>
      {icon && <Icon name={icon} size={13} />}
      {children}
    </button>
  );
}

function PillarChip({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-right">
      <div className="text-[9.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/50 font-bold">{label}</div>
      <div className={`text-[12px] tabular-nums font-bold ${value >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{fmtSigned(value)}</div>
    </div>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success' | 'info'; children: React.ReactNode }) {
  const m = tone === 'error'
    ? 'bg-rose-50 dark:bg-rose-500/10 ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200'
    : tone === 'success' ? 'bg-emerald-50 dark:bg-emerald-500/10 ring-emerald-200/60 dark:ring-emerald-400/30 text-emerald-700 dark:text-emerald-200'
    : 'bg-arena-50/70 dark:bg-arena-800/60 ring-arena-200/60 dark:ring-arena-600/40 text-arena-800 dark:text-neon-200/80';
  return <div className={`rounded-2xl ring-1 ring-inset px-4 py-3 text-[13px] ${m}`}>{children}</div>;
}

function EmptyState({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-arena-700/40
                    ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                    shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                    px-6 py-12 text-center backdrop-blur">
      <div className="mx-auto grid place-items-center w-14 h-14 rounded-2xl
                      bg-arena-100/60 dark:bg-arena-800/60 mb-3">
        <Icon name={icon} size={26} className="text-arena-400 dark:text-neon-200/40" />
      </div>
      <div className="font-bold text-arena-900 dark:text-white">{title}</div>
      <div className="text-[12px] text-arena-600 dark:text-neon-200/60 mt-1 max-w-sm mx-auto leading-relaxed">{subtitle}</div>
    </div>
  );
}
